import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Toaster } from '../components/ui/sonner';
import { toast as sonnerToast } from 'sonner';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const services = [
    'Cloud Migration',
    'Cloud Security & Compliance',
    'Data & AI/ML Solutions',
    'Cost Optimization',
    'AWS Managed Services',
    'DevOps Automation',
    'E-commerce Solutions',
    'FinTech Solutions',
    'Gaming Solutions',
    'Manufacturing Solutions',
    'General Inquiry',
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      sonnerToast.success('Thank you for contacting us!', {
        description: 'Our team will get back to you within 24 hours.',
      });
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        service: '',
        message: '',
      });
      setIsSubmitting(false);
    }, 1000);
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email',
      value: 'axoracloudservice@gmail.com',
      link: 'mailto:axoracloudservice@gmail.com',
    },
    {
      icon: Phone,
      title: 'Phone',
      value: '03 2022 4476',
      link: 'tel:0320224476',
    },
    {
      icon: MapPin,
      title: 'Offices',
      isMultiLine: true,
      addresses: [
        {
          location: 'KL',
          address: 'Level 20-02 Menara Hap Seng 2, No. 1 & 3, Jalan P. Ramlee, 50250 Kuala Lumpur, Malaysia.'
        },
        {
          location: 'SG',
          address: '62 UBI ROAD 1, #08-06, OXLEY BIZHUB 2, SINGAPORE 408734'
        }
      ],
      link: '#',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#020B1A] to-[#061426]">
      <Navbar />
      <Toaster />
      
      <section className="relative bg-gradient-to-br from-[#020B1A] via-[#061426] to-[#0A1E3F] text-white pt-32 pb-24 mt-16 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-96 h-96 bg-cyan-500/30 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-600/30 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Business & Partnership Enquiries
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Contact us regarding future cloud service requirements,
              technology collaboration or business partnership opportunities.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-b from-[#061426] to-[#020B1A]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2 className="text-3xl font-bold text-white mb-8">Contact Information</h2>
              <div className="space-y-6">
                {contactInfo.map((info, index) => {
                  const Icon = info.icon;
                  return (
                    <a
                      key={index}
                      href={info.link}
                      className="flex items-start space-x-4 p-4 rounded-lg bg-gradient-to-br from-[#061426] to-[#0A1E3F] border border-cyan-500/20 hover:border-cyan-400 hover:bg-[#0A1E3F] transition-all group"
                    >
                      <div className="w-12 h-12 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 border border-cyan-500/30 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:border-cyan-400 transition-colors" style={{ boxShadow: '0 0 15px rgba(0, 212, 255, 0.2)' }}>
                        <Icon className="w-6 h-6 text-cyan-400 group-hover:text-cyan-300 transition-colors" />
                      </div>
                      <div>
                        <h3 className="font-bold text-white mb-1">{info.title}</h3>
                        {info.isMultiLine ? (
                          <div className="space-y-3">
                            {info.addresses.map((addr, idx) => (
                              <div key={idx} className="text-sm">
                                <span className="font-semibold text-cyan-400">{addr.location}:</span>
                                <p className="text-gray-400 leading-relaxed mt-1">{addr.address}</p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-gray-400 text-sm leading-relaxed">{info.value}</p>
                        )}
                      </div>
                    </a>
                  );
                })}
              </div>

              <div className="mt-12 p-6 bg-gradient-to-br from-cyan-500/10 to-blue-600/10 border border-cyan-500/30 rounded-xl text-white">
                <h3 className="text-xl font-bold mb-3 text-cyan-400">Business Hours:</h3>
                <p className="text-gray-300">
                  Mon – Fri: 9:00 AM – 6:00 PM
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-gradient-to-br from-[#061426] to-[#0A1E3F] border-2 border-cyan-500/30 rounded-xl p-8">
                <h2 className="text-3xl font-bold text-white mb-2">Send Us a Message</h2>
                <p className="text-gray-400 mb-8">Fill out the form below and we'll get back to you within 24 hours.</p>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                        Full Name *
                      </label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Doe"
                        className="w-full bg-[#020B1A] border-cyan-500/30 text-white placeholder:text-gray-500 focus:border-cyan-400"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                        Email Address *
                      </label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@company.com"
                        className="w-full bg-[#020B1A] border-cyan-500/30 text-white placeholder:text-gray-500 focus:border-cyan-400"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-gray-300 mb-2">
                        Company Name
                      </label>
                      <Input
                        id="company"
                        name="company"
                        type="text"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Your Company"
                        className="w-full bg-[#020B1A] border-cyan-500/30 text-white placeholder:text-gray-500 focus:border-cyan-400"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                        Phone Number
                      </label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+60 12-345 6789"
                        className="w-full bg-[#020B1A] border-cyan-500/30 text-white placeholder:text-gray-500 focus:border-cyan-400"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="service" className="block text-sm font-medium text-gray-300 mb-2">
                      Service Interested In *
                    </label>
                    <select
                      id="service"
                      name="service"
                      required
                      value={formData.service}
                      onChange={handleChange}
                      className="w-full px-3 py-2 bg-[#020B1A] border border-cyan-500/30 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-400"
                    >
                      <option value="" className="bg-[#020B1A]">Select a service</option>
                      {services.map((service, index) => (
                        <option key={index} value={service} className="bg-[#020B1A]">
                          {service}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                      Message *
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your project and requirements..."
                      rows={6}
                      className="w-full bg-[#020B1A] border-cyan-500/30 text-white placeholder:text-gray-500 focus:border-cyan-400"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-medium py-6 text-lg transition-all duration-300 disabled:opacity-50"
                    style={{ boxShadow: '0 0 20px rgba(0, 212, 255, 0.4)' }}
                  >
                    {isSubmitting ? (
                      'Sending...'
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
